
import React from "react";
import {
  CalendarClock,
  Copy,
  Users,
  Star,
  RefreshCw,
  Save,
  FolderPlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import MetricCard from "@/components/admin/metrics/MetricCard";
import TimeFrameSelector from "@/components/admin/metrics/TimeFrameSelector";
import PromptTimeFrameSelector from "@/components/admin/metrics/PromptTimeFrameSelector";
import {
  UserGrowthChart,
  PromptInteractionsChart,
  RatingDistributionChart,
  PromptCopiesChart,
  PromptSavesChart,
  PromptCollectionsChart
} from "@/components/admin/metrics/MetricsCharts";
import TopPromptsTable from "@/components/admin/metrics/TopPromptsTable";
import { useMetricsDashboard } from "@/hooks/useMetricsDashboard";

interface AdminDashboardProps {
  pendingPrompts?: number;
  totalUsers?: number;
  totalCategories?: number;
  reportedIssues?: number;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  pendingPrompts,
  totalUsers,
  totalCategories,
  reportedIssues,
}) => {
  const {
    userTimeFrame,
    promptTimeFrame,
    userDateRange,
    promptDateRange,
    isLoading,
    isRefreshing,
    userMetrics,
    promptMetrics,
    ratingMetrics,
    topPrompts,
    handleUserTimeFrameChange,
    handlePromptTimeFrameChange,
    setUserDateRange,
    setPromptDateRange,
    refreshData,
  } = useMetricsDashboard();

  // Check if we have any metrics data at all
  const hasAnyMetrics = userMetrics !== null || 
                        promptMetrics !== null || 
                        ratingMetrics !== null || 
                        (topPrompts && topPrompts.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-2xl font-semibold">Analytics Dashboard</h2>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={refreshData}
            disabled={isLoading || isRefreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* User Metrics Section with its own time filter */}
      {(userMetrics || isLoading) && (
        <>
          <div className="flex flex-col md:flex-row md:items-center justify-between mt-6 mb-2">
            <h3 className="text-lg font-medium">User Growth Metrics</h3>
            <TimeFrameSelector
              timeFrame={userTimeFrame}
              onTimeFrameChange={handleUserTimeFrameChange}
              dateRange={userDateRange}
              onDateRangeChange={setUserDateRange}
            />
          </div>
        
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(userMetrics?.totalUsers || isLoading) && (
              <MetricCard 
                title="Total Users"
                value={userMetrics?.totalUsers?.value || totalUsers || 0}
                metric={userMetrics?.totalUsers}
                icon={<Users className="h-5 w-5" />}
                tooltip="Total number of registered users"
                isLoading={isLoading}
              />
            )}
            
            {(userMetrics?.newUsers || isLoading) && (
              <MetricCard 
                title="New Users"
                value={userMetrics?.newUsers?.value || 0}
                metric={userMetrics?.newUsers}
                icon={<Users className="h-5 w-5" />}
                tooltip={`New accounts created in the ${userTimeFrame} period`}
                isLoading={isLoading}
              />
            )}
          </div>
        </>
      )}
      
      {/* User Growth Chart */}
      {(userMetrics?.userGrowthData?.length > 0 || isLoading) && (
        <UserGrowthChart 
          data={userMetrics?.userGrowthData || []}
          isLoading={isLoading}
        />
      )}
      
      {/* Prompt Interactions Section with its own time filter */}
      {((promptMetrics) || isLoading) && (
        <>
          <div className="flex flex-col md:flex-row md:items-center justify-between mt-8 mb-2 pt-2 border-t">
            <h3 className="text-lg font-medium">Prompt Engagement Metrics</h3>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
              <PromptTimeFrameSelector
                timeFrame={promptTimeFrame}
                onTimeFrameChange={handlePromptTimeFrameChange}
                dateRange={promptDateRange}
                onDateRangeChange={setPromptDateRange}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Prompt Copies Metric */}
            {(promptMetrics?.totalCopies || isLoading) && (
              <MetricCard 
                title="Prompt Copies"
                value={promptMetrics?.totalCopies?.value || 0}
                metric={promptMetrics?.totalCopies}
                icon={<Copy className="h-5 w-5" />}
                tooltip={`All-time prompt copies: ${promptMetrics?.allTimeCopies || 0}. Showing growth for the current time period.`}
                isLoading={isLoading}
                onRefresh={refreshData}
              />
            )}
            
            {/* Prompt Saves Metric */}
            {(promptMetrics?.totalSaves || isLoading) && (
              <MetricCard 
                title="Prompt Saves"
                value={promptMetrics?.totalSaves?.value || 0}
                metric={promptMetrics?.totalSaves}
                icon={<Save className="h-5 w-5" />}
                tooltip={`All-time saved prompts: ${promptMetrics?.allTimeSaves || 0}. Showing growth for the current time period.`}
                isLoading={isLoading}
                onRefresh={refreshData}
              />
            )}
            
            {/* Collection Adds Metric */}
            {(promptMetrics?.totalCollections || isLoading) && (
              <MetricCard 
                title="Collection Adds"
                value={promptMetrics?.totalCollections?.value || 0}
                metric={promptMetrics?.totalCollections}
                icon={<FolderPlus className="h-5 w-5" />}
                tooltip={`All-time prompts added to collections: ${promptMetrics?.allTimeCollections || 0}. Showing growth for the current time period.`}
                isLoading={isLoading}
                onRefresh={refreshData}
              />
            )}
          </div>
        </>
      )}
      
      {/* Combined Prompt Interactions Chart */}
      {(promptMetrics?.interactionData?.length > 0 || isLoading) && (
        <PromptInteractionsChart 
          data={promptMetrics?.interactionData || []}
          isLoading={isLoading}
        />
      )}

      {/* Individual Metric Charts Section */}
      {((promptMetrics) || isLoading) && (
        <>
          <div className="flex items-center justify-between mt-8 mb-3 pt-2 border-t">
            <h3 className="text-lg font-medium">Detailed Metrics</h3>
            <span className="text-xs text-muted-foreground">Individual metric trends</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            {/* Individual Copy Chart */}
            {(promptMetrics?.copiesData?.length > 0 || isLoading) && (
              <PromptCopiesChart 
                data={promptMetrics?.copiesData || []}
                isLoading={isLoading}
              />
            )}
            
            {/* Individual Saves Chart */}
            {(promptMetrics?.savesData?.length > 0 || isLoading) && (
              <PromptSavesChart 
                data={promptMetrics?.savesData || []}
                isLoading={isLoading}
              />
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            {/* Individual Collections Chart */}
            {(promptMetrics?.collectionsData?.length > 0 || isLoading) && (
              <PromptCollectionsChart 
                data={promptMetrics?.collectionsData || []}
                isLoading={isLoading}
              />
            )}
            
            {/* Rating distribution takes the other half of the row if available */}
            {(ratingMetrics?.ratingDistribution?.length > 0 || isLoading) && (
              <RatingDistributionChart 
                data={ratingMetrics?.ratingDistribution || []}
                isLoading={isLoading}
              />
            )}
          </div>
        </>
      )}
      
      {/* Rating Metrics Section */}
      {(ratingMetrics || isLoading) && ratingMetrics?.ratingDistribution?.length === 0 && (
        <>
          <div className="flex items-center justify-between mt-8 mb-3 pt-2 border-t">
            <h3 className="text-lg font-medium">Rating Metrics</h3>
            <span className="text-xs text-muted-foreground">Quality indicators</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(ratingMetrics?.averageRating || isLoading) && (
              <MetricCard 
                title="Average Rating"
                value={(ratingMetrics?.averageRating?.value || 0).toFixed(1)}
                metric={ratingMetrics?.averageRating}
                icon={<Star className="h-5 w-5" />}
                tooltip="Average rating across all prompts"
                isLoading={isLoading}
              />
            )}
          </div>
        </>
      )}
      
      {/* Top Prompts Section */}
      {(topPrompts?.length > 0 || isLoading) && (
        <>
          <div className="flex items-center justify-between mt-8 mb-3 pt-2 border-t">
            <h3 className="text-lg font-medium">Top Performing Prompts</h3>
          </div>
          <TopPromptsTable 
            data={topPrompts || []}
            isLoading={isLoading}
          />
        </>
      )}

      {/* Show a message if no metrics data is available */}
      {!isLoading && !hasAnyMetrics && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-muted p-4 mb-4">
            <RefreshCw className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-medium mb-2">No metrics data available</h3>
          <p className="text-muted-foreground max-w-md">
            There's no analytics data to display for the selected time period. Try changing the time frame or 
            waiting until users interact with the platform.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
